import moment from 'moment-timezone';
import { displayMoment, displayNow } from '../../utils/displayTime';
import { isCountryVietnam } from '../../constants/countries';

/**
 * The note list shows a relative/calendar label next to each note and the full timestamp in its
 * tooltip. Both have to describe the same instant in the same zone, so they live here together
 * rather than being formatted separately at the two JSX call sites.
 *
 * Kept as plain JS (no JSX) so the calendar decisions can be tested directly: the Jest transform
 * in this repo does not compile JSX.
 */

/**
 * The label under the note author. `language` drives Moment's global locale exactly as before —
 * month names and `fromNow()` wording are translated by it.
 */
export function formatNoteTimestamp(timestamp, countryCode,
  { language, atLabel, yesterdayAtLabel }) {
  // Deliberately unchanged from the original note list: this sets Moment's *global* locale from
  // the interface language, and the surrounding screens have always depended on that side effect.
  // Narrowing it to this value alone would be a separate, wider change, so it is preserved as-is.
  moment.locale(language);
  const now = displayNow(countryCode);
  const targetDate = displayMoment(timestamp, countryCode);
  if (now.isSame(targetDate, 'day')) {
    return targetDate.fromNow();
  }
  // `subtract` mutates, and the original code relied on `now` staying mutated for the year check
  // below. Vietnam needs an intact `now` so that check runs on the viewer's own calendar year;
  // MY/ID keep the existing reference so their output does not change at all.
  const yesterdayReference = isCountryVietnam(countryCode) ? now.clone() : now;
  if (yesterdayReference.subtract(1, 'days').isSame(targetDate, 'day')) {
    return targetDate.format(`[${yesterdayAtLabel}] h:mm A`);
  }
  if (now.isSame(targetDate, 'year')) {
    return targetDate.format(`MMMM Do [${atLabel}] h:mm A`);
  }
  return targetDate.format(`MMMM Do YYYY [${atLabel}] h:mm A`);
}

/**
 * The hover tooltip. `atLabel` differs per call site — the created tooltip is translated, the
 * edited one has always used a literal "at" — so it stays a caller decision.
 */
export function formatNoteTooltip(timestamp, countryCode, atLabel) {
  return displayMoment(timestamp, countryCode).format(`dddd, MMMM Do YYYY [${atLabel}] h:mm A`);
}



// WEBPACK FOOTER //
// ./src/components/BookingNote/noteTimestamp.js